import { featureCollection } from '@turf/turf';
import { useContext, useState } from 'react';
import { useParams } from 'react-router';
import { Button, ButtonGroup, ButtonToolbar, Label } from 'reactstrap';

import {
  CLU_BOUNDARIES_LAYER_ID,
  mapModes,
  SOIL_BOUNDARIES_LAYER_ID,
} from '../../constants/map';
import { MapContext } from '../../contexts/MapContext';
import { SUITABILITY_LAYER_ID } from '../right-menu/SuitabilityMenu';
import { BASE_LAYER_ID } from '../right-menu/BaseLayerMenu';
import MaxWidthDiv from '../basics/MaxWidthDiv';
import DeletingToolbar from './DeletingToolbar';
import ImportLayerButton from './buttons/ImportLayerButton';
import CluLayerToolbar from './CluLayerToolbar';
import ButtonWithShortcut from '../basics/ButtonWithShortcut';
import { SHORTCUTS } from '../../constants/shortcuts';
import { layerHasData, layerIsCustom } from '../../utils/layers';
import DropPinButton from './buttons/DropPinButton';
import LocationIcon from '../icons/LocationIcon';
import LineIcon from '../icons/LineIcon';
import PolygonIcon from '../icons/PolygonIcon';
import { captureEvent } from '../../utils/analytics';
import SoilLayerToolbar from './SoilLayerToolbar';
import { LAND_USE_LAYER_ID } from '../right-menu/LandUseLayerMenu';
import { CLIMATE_LAYER_ID } from '../right-menu/ClimateLayerMenu';
import SwitchInput from '../basics/SwitchInput';
import { useTilesetContext } from '../../contexts/TilesetContext';

const TILESET_LAYER_IDS = [
  SUITABILITY_LAYER_ID,
  LAND_USE_LAYER_ID,
  CLIMATE_LAYER_ID,
];

const STATIC_LAYER_IDS = [...TILESET_LAYER_IDS, BASE_LAYER_ID];

const DRAW_MODE_BUTTONS = [
  {
    mode: 'draw_point',
    icon: (
      <LocationIcon height={16} width={16} style={{ margin: '-1px 0 0' }} />
    ),
    shortcutKey: SHORTCUTS.CUSTOM_LAYER_DRAW_POINT.key,
  },
  {
    mode: 'draw_line_string',
    icon: <LineIcon height={20} width={20} style={{ margin: '-2px -2px 0' }} />,
    shortcutKey: SHORTCUTS.CUSTOM_LAYER_DRAW_LINE.key,
  },
  {
    mode: 'draw_polygon',
    icon: (
      <PolygonIcon height={14} width={14} style={{ margin: '-1px 1px 0' }} />
    ),
    shortcutKey: SHORTCUTS.CUSTOM_LAYER_DRAW_POLYGON.key,
  },
];

const LayerToolbar = () => {
  const { farmId } = useParams();
  const {
    mapState: {
      mapMode,
      setMapMode,
      activeLayerId,
      activeLayer,
      layerDrawModeConfig,
      setLayerDrawMode: setDrawMode,
    },
    layers: { layers, setLayer, setLayerData, deleteCustomLayer },
  } = useContext(MapContext);
  const { tileset, tilesetMasked, setTilesetMasked } = useTilesetContext();
  const [drawMode] = layerDrawModeConfig;

  const [deletingLayer, setDeletingLayer] = useState(false);

  if (deletingLayer) {
    return (
      <DeletingToolbar
        fullText={
          <>
            Are you sure you want to delete all features in{' '}
            <b>{activeLayer.displayName}</b>?
          </>
        }
        onCancel={() => {
          setDeletingLayer(false);
        }}
        onConfirm={() => {
          if (layerIsCustom(activeLayer)) {
            // We change the map mode first, so that if we're in edit mode, the
            // useDrawLayer useEffect cleanup is called before the layer is
            // deleted.
            setMapMode(mapModes.VIEW);

            deleteCustomLayer(activeLayer);
            captureEvent('custom-layer-delete');
          } else {
            setLayer(activeLayerId, {
              featuresShown: {},
              layerFeatureRecords: {},
            });
            setLayerData({
              layerId: activeLayerId,
              farmId,
              data: featureCollection([]),
            });
          }
          setDeletingLayer(false);
        }}
      />
    );
  }

  const layerHasFeatures = !!activeLayer?.data?.features?.length;
  const hasFarmBoundary = !!layers.BOUNDARY?.data?.features?.length;

  return (
    <ButtonToolbar className="d-flex align-items-center">
      {activeLayer?.displayName && (
        <MaxWidthDiv
          className="mr-2 font-weight-bold"
          title={activeLayer?.displayName}
        >
          {activeLayer?.displayName}
        </MaxWidthDiv>
      )}

      {activeLayerId === CLU_BOUNDARIES_LAYER_ID && <CluLayerToolbar />}
      {activeLayerId === SOIL_BOUNDARIES_LAYER_ID && <SoilLayerToolbar />}

      {!activeLayer?.disableUserEdits && activeLayer?.drawTool && (
        <ButtonGroup className="mr-2">
          <ButtonWithShortcut
            shortcut={SHORTCUTS.DRAW.key}
            onClick={() => {
              setMapMode(mapModes.DRAW_LAYER);
            }}
            color={mapMode === mapModes.DRAW_LAYER ? 'primary' : 'secondary'}
            size="sm"
            outline={mapMode !== mapModes.DRAW_LAYER}
          >
            Draw
          </ButtonWithShortcut>

          {layerHasFeatures && (
            <ButtonWithShortcut
              shortcut={SHORTCUTS.EDIT.key}
              onClick={() => setMapMode(mapModes.EDIT_LAYER)}
              color={mapMode === mapModes.EDIT_LAYER ? 'primary' : 'secondary'}
              size="sm"
              outline={mapMode !== mapModes.EDIT_LAYER}
            >
              Edit
            </ButtonWithShortcut>
          )}
        </ButtonGroup>
      )}

      {/* Custom layer edit mode buttons */}
      {layerIsCustom(activeLayer) && mapMode === mapModes.DRAW_LAYER && (
        <ButtonGroup className="mr-2">
          {DRAW_MODE_BUTTONS.map(({ mode, icon, shortcutKey }) => (
            <ButtonWithShortcut
              key={mode}
              outline
              size="sm"
              onClick={() => {
                captureEvent('custom-layer-draw-mode-click', { mode });
                setDrawMode(mode);
              }}
              shortcut={shortcutKey}
              active={mode === drawMode}
            >
              {icon}
            </ButtonWithShortcut>
          ))}
        </ButtonGroup>
      )}

      {layerIsCustom(activeLayer) && <DropPinButton className="mr-2" />}

      {!STATIC_LAYER_IDS.includes(activeLayerId) && (
        <>
          {!activeLayer?.disableUserEdits && <ImportLayerButton />}
          {layerHasData(activeLayer) && (
            <Button
              size="sm"
              className="mr-2"
              outline
              onClick={() => setDeletingLayer(true)}
            >
              {layerIsCustom(activeLayer) ? 'Delete Layer' : 'Delete All'}
            </Button>
          )}
        </>
      )}
      {TILESET_LAYER_IDS.includes(activeLayerId) && hasFarmBoundary && (
        <>
          <Label className="my-0 mx-2">Restrict to farm boundary</Label>
          <SwitchInput
            checked={tilesetMasked}
            className="mr-2"
            disabled={tileset == null}
            onChange={() => setTilesetMasked(!tilesetMasked)}
          />
        </>
      )}
      <ButtonWithShortcut
        shortcut={SHORTCUTS.CLOSE.key}
        onClick={() => setMapMode(mapModes.VIEW)}
        color="primary"
        size="sm"
      >
        Done
      </ButtonWithShortcut>
    </ButtonToolbar>
  );
};

export default LayerToolbar;
